import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Grid,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import QrCodeIcon from "@mui/icons-material/QrCode";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import sampleQRImage from "./SampleFiles/Blank.png";
import { useAuth } from "../../hooks/AuthProvider";
import {
  GENERATE_QR_GET_NUMBER_ENDPOINT,
  GENERATE_QR_ENDPOINT,
  SAVE_QR_CONFIGURATION_ENDPOINT,
  GET_QR_CONFIGURATION_ENDPOINT,
} from "../../config/urls";
import ButtonOne from "../../common/normal-button";
import typography from "../../cssUtils/typography";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";

// Phone Library Imports
import {
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input/input";
import en from "react-phone-number-input/locale/en.json";
import parsePhoneNumber from "libphonenumber-js";

// Build country options (ISO code -> label + dial code)
const countryOptions = getCountries().map((country) => ({
  isoCode: country,
  label: en[country],
  dialCode: `+${getCountryCallingCode(country)}`,
}));

// Preferred ISO for calling codes shared by multiple countries
const CALLING_CODE_PREFERRED_COUNTRY = {
  "1": "US",
  "7": "RU",
  "44": "GB",
  "61": "AU",
  "64": "NZ",
};

const getCountryFromCallingCode = (callingCode) => {
  if (!callingCode) return null;
  const code = String(callingCode);
  if (CALLING_CODE_PREFERRED_COUNTRY[code]) return CALLING_CODE_PREFERRED_COUNTRY[code];
  return getCountries().find(
    (country) => String(getCountryCallingCode(country)) === code,
  ) || null;
};

const GenerateQRForm = () => {
  const { getValidToken } = useAuth();

  const [formState, setFormState] = useState({
    country_iso: "IN",
    phoneNumber: "",
    message: "",
    color: "#000000",
  });

  const [error, setError] = useState("");
  const [qrSuccessMessage, setqrSuccessMessage] = useState("");
  const [qrErrorMessage, setqrErrorMessage] = useState("");
  const [contentAccordionExpanded, setContentAccordionExpanded] =
    useState(true);
  const [logoAccordionExpanded, setLogoAccordionExpanded] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [qrFileUrl, setQrFileUrl] = useState(sampleQRImage);
  const [imageUrl, setImageUrl] = useState("");

  const constructUrl = () => {
    // Construct the dial code from the ISO code
    const dialCode = getCountryCallingCode(formState.country_iso);
    const phone = formState.phoneNumber.replace(/\D/g, "");
    const msg = encodeURIComponent(formState.message);
    return `https://api.whatsapp.com/send/?phone=${dialCode}${phone}&text=${msg}`;
  };

  const isValidMessage = (msg) => {
    const regex = /^[a-zA-Z0-9\s,()]{1,30}$/;
    return regex.test(msg);
  };

  const handleGenerateQRCode = async (skipSuccessMessage) => {
    const finalUrl = constructUrl();
    const phoneError = validatePhoneNumber(
      formState.phoneNumber,
      formState.country_iso,
    );

    if (phoneError) {
      setError(phoneError);
      return;
    }
    if (!isValidMessage(formState.message)) {
      setError("Message must be alphanumeric and max 30 characters");
      return;
    }
    setError("");
    const formData = new FormData();
    formData.append("url", finalUrl);
    formData.append("fill_color", formState.color);
    if (logoFile) {
      formData.append("file", logoFile);
    }

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    fetch(GENERATE_QR_ENDPOINT, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": tenant,
      },
      body: formData,
    })
      .then((response) => {
        if (response.ok) {
          if (!skipSuccessMessage) {
            setqrSuccessMessage("QR code generated successfully");
          }
          setqrErrorMessage("");
        } else {
          setqrErrorMessage("Failed to generate QR code");
        }
        return response.json();
      })
      .then((data) => {
        if (data.qr_file_url) setQrFileUrl(data.qr_file_url);
      })
      .catch((error) => console.error("Error:", error));
  };

  const handleDownloadQRCode = () => {
    if (qrFileUrl === sampleQRImage) {
      setqrErrorMessage("QR code not generated");
    } else {
      const tenant = localStorage.getItem("tenant");
      fetch(qrFileUrl, { headers: { "X-Tenant-ID": tenant } })
        .then((response) => response.blob())
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "QR_code.png";
          a.click();
          URL.revokeObjectURL(url);
        });
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) setLogoFile(file);
  };

  const getExpectedDigitCount = (isoCode) => {
    try {
      const example = getExampleNumber(isoCode, examples);
      return example?.nationalNumber?.length || null;
    } catch {
      return null;
    }
  };

  const validatePhoneNumber = (phone, country_iso) => {
    if (!phone) return "Please enter a phone number";

    const expectedDigits = getExpectedDigitCount(country_iso);

    if (expectedDigits && phone.length !== expectedDigits) {
      return `Enter a valid ${country_iso} phone number (${expectedDigits} digits)`;
    }

    const fullNumber = `+${getCountryCallingCode(country_iso)}${phone}`;
    console.log("FPN: ", fullNumber);
    if (!isPossiblePhoneNumber(fullNumber)) {
      return "Invalid phone number";
    }

    return "";
  };

  const handleDeleteLogo = () => setLogoFile(null);

  const handleSave = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(SAVE_QR_CONFIGURATION_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country_code: formState.country_iso,
          phone_number: formState.phoneNumber,
          prefilled_message: formState.message,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setqrSuccessMessage("Configuration saved successfully");
        setqrErrorMessage("");
        // Regenerate the QR using the just-saved phone/message so Download reflects the new config
        await handleGenerateQRCode(true);
      } else {
        setqrSuccessMessage("");
        setqrErrorMessage(data.message || "Failed to save configuration");
      }
    } catch (error) {
      console.error("Error saving configuration:", error);
      setqrSuccessMessage("");
      setqrErrorMessage("Failed to save configuration");
    }
  };

  const fetchSavedConfiguration = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(GET_QR_CONFIGURATION_ENDPOINT, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
        },
      });

      if (!response.ok) return;

      const data = await response.json();

      setFormState((prev) => ({
        ...prev,
        country_iso: data.country_code,
        phoneNumber: data.phone_number,
        message: data.prefilled_message,
      }));
    } catch (error) {
      console.error("Error fetching saved configuration:", error);
    }
  };


  useEffect(() => {
    const tenant = localStorage.getItem("tenant");

    if (qrFileUrl && qrFileUrl !== sampleQRImage) {
      fetch(qrFileUrl, {
        headers: {
          "X-Tenant-ID": tenant,
        },
      })
        .then((response) => response.blob())
        .then((blob) => {
          setImageUrl(URL.createObjectURL(blob));
        })
        .catch(() => {
          setImageUrl(sampleQRImage);
        });
    } else {
      setImageUrl(sampleQRImage);
    }
  }, [qrFileUrl]);

  useEffect(() => {
    const fetchBusinessNumber = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const response = await fetch(GENERATE_QR_GET_NUMBER_ENDPOINT, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenant,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const rawFullNumber = data.business_wa_number;

          if (rawFullNumber) {
            // Add a '+' if it's missing so the library can parse it
            const formatted = rawFullNumber.startsWith("+")
              ? rawFullNumber
              : `+${rawFullNumber}`;
            const parsed = parsePhoneNumber(formatted);

            if (parsed) {
              setFormState((prev) => ({
                ...prev,
                country_iso: parsed.country || getCountryFromCallingCode(parsed.countryCallingCode) || "IN",
                phoneNumber: parsed.nationalNumber,
                
              }));
            } else {
              // Fallback if parsing fails
              setFormState((prev) => ({
                ...prev,
                phoneNumber: rawFullNumber,
                
              }));
            }
          }

          if (data.qr_file_url) setQrFileUrl(data.qr_file_url);
        }
        await fetchSavedConfiguration();
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    
    fetchBusinessNumber();
  }, []);

  return (
    <Box sx={{ width: "100%", bgcolor: "transparent" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          width: "100%",
        }}
      >
        <Box
          sx={{ flex: { md: "0 0 60%" }, width: { xs: "100%", md: "auto" } }}
        >
          <Accordion
            expanded={contentAccordionExpanded}
            onChange={(_, isExpanded) =>
              setContentAccordionExpanded(isExpanded)
            }
            sx={{ borderRadius: "8px", "&:before": { display: "none" }, mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Enter Content</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Country ISO Selection */}
                <Grid item xs={4}>
                  <Typography
                    sx={{
                      ...typography.bodySmall,
                      color: "#6C7C93",
                      mb: 0.5,
                      ml: 0.5,
                    }}
                  >
                    Country Code*
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={formState.country_iso}
                    onChange={(e) =>
                      setFormState({
                        ...formState,
                        country_iso: e.target.value,
                      })
                    }
                  >
                    {countryOptions.map(({ isoCode, dialCode }) => (
                      <MenuItem key={isoCode} value={isoCode}>
                        {isoCode} ({dialCode})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Phone Number (National Only) */}
                <Grid item xs={8}>
                  <Typography
                    sx={{
                      ...typography.bodySmall,
                      color: "#6C7C93",
                      mb: 0.5,
                      ml: 0.5,
                    }}
                  >
                    Phone number*
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="9999999999"
                    value={formState.phoneNumber}
                    onChange={(e) => {
                      const expectedDigits = getExpectedDigitCount(
                        formState.country_iso,
                      );
                      const raw = e.target.value.replace(/\D/g, "");
                      if (
                        expectedDigits &&
                        raw.length > expectedDigits
                      ) {
                        return;
                      }
                      setFormState({ ...formState, phoneNumber: raw });
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    sx={{
                      ...typography.bodySmall,
                      color: "#6C7C93",
                      mb: 0.5,
                      ml: 0.5,
                    }}
                  >
                    Pre-filled message
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={formState.message}
                    onChange={(e) =>
                      setFormState({ ...formState, message: e.target.value })
                    }
                    inputProps={{ maxLength: 30 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "right",
                      mt: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    {formState.message.length}/30
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={logoAccordionExpanded}
            onChange={(_, isExpanded) => setLogoAccordionExpanded(isExpanded)}
            sx={{ borderRadius: "8px", "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 500 }}>Add Logo Image</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 2,
                  p: 1,
                  px: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <input
                    accept="image/jpeg,image/png"
                    style={{ display: "none" }}
                    id="logo-upload"
                    type="file"
                    onChange={handleLogoUpload}
                  />

                  {/* Using ButtonOne for uniformity */}
                  <ButtonOne
                    variant="outlined"
                    component="label" // Allows the button to act as the label for the input
                    htmlFor="logo-upload"
                    startIcon={<FileUploadIcon />}
                  >
                    Upload PNG or JPG
                  </ButtonOne>

                  {logoFile && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        bgcolor: "#F5F7FA",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "6px",
                        border: "1px solid #E5E7EB",
                        height: 40, // Matches button height
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#04142F",
                          maxWidth: "150px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          mr: 1,
                        }}
                      >
                        {logoFile.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleDeleteLogo}
                        sx={{ color: "#FF4D4F", p: 0.5 }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
        </Box>

        <Box
          sx={{ flex: { md: "0 0 35%" }, width: { xs: "100%", md: "auto" } }}
        >
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <img
              src={imageUrl || sampleQRImage}
              alt="QR Code"
              style={{ width: 264, height: 264, objectFit: "contain" }}
            />
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <ButtonOne
            variant="contained"
            color="primary"
            onClick={() => handleGenerateQRCode()}
            startIcon={<QrCodeIcon />}
          >
            Generate QR Code
            </ButtonOne>
            <ButtonOne
              variant="contained"
              color="primary"
              onClick={handleDownloadQRCode}
              startIcon={<DownloadIcon />}
            >
              Download QR Code
            </ButtonOne>
            <ButtonOne
              variant="contained"
              color="primary"
              onClick={handleSave}
              startIcon={<SaveIcon />}
            >
              Save
            </ButtonOne>
          </Box>

          {qrSuccessMessage && (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              onClose={() => setqrSuccessMessage("")}
            >
              {qrSuccessMessage}
            </Alert>
          )}
          {qrErrorMessage && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setqrErrorMessage("")}
            >
              {qrErrorMessage}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default GenerateQRForm;
